/**
 * resource_catalog.ts — the ORIGINAL resource taxonomy (SWG-inspired
 * mechanics, atlas-native content): 4 classes × 15 families, 8 stats
 * rolled 1–1000 per SPAWN (a named, rotating instance of a family), and
 * the deterministic CONCENTRATION field every client and the server agree
 * on. Resources exist ONLY in the commons world.
 *
 * CLIENT MIRROR of server/src/lib/resource_catalog.ts (the versona_names
 * law — same hash, same tables; edit BOTH or the survey lies).
 */
// MIRROR of server/src/lib/resource_catalog.ts — keep tables + math byte-equivalent.
function fnv(s: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) { h ^= s.charCodeAt(i); h = Math.imul(h, 0x01000193); }
  return h >>> 0;
}

/** Stat keys (1–1000): purity, density, resilience, flexibility,
 *  volatility, nutrience, luster, resonance. */
export const RES_STATS = ['PUR', 'DEN', 'RES', 'FLX', 'VOL', 'NUT', 'LUS', 'RSN'] as const;
export type ResStat = (typeof RES_STATS)[number];

export type ResClass = 'minerals' | 'chemicals' | 'food' | 'materials';

export interface ResFamily {
  key: string;
  cls: ResClass;
  label: string;
  /** Stats this family rolls (others stay 0 — the SWG "n/a" dash). */
  stats: ResStat[];
  /** Name roots the spawn namer draws from (never franchise terms). */
  roots: string[];
  /** Which settlement commodity a haul lands as. ONE PER FAMILY since the society wave
   *  (2026-09-19): fifteen families used to collapse into eight shelf keys, so a versona
   *  shelf could not tell noble ore from crystal and could never fill a circuit lattice. */
  commodity: string;
}

export const RES_FAMILIES: ResFamily[] = [
  // ── minerals ──
  { key: 'ferrite_ore', cls: 'minerals', label: 'Ferrite Ore', stats: ['PUR', 'DEN', 'RES'], roots: ['Duskiron', 'Greyvane', 'Cobalite', 'Marrowsteel'], commodity: 'ore' },
  { key: 'noble_ore', cls: 'minerals', label: 'Noble Ore', stats: ['PUR', 'LUS'], roots: ['Palegold', 'Sunmarrow', 'Aurelium'], commodity: 'noble_ore' },
  { key: 'crystal', cls: 'minerals', label: 'Crystal', stats: ['RSN', 'PUR', 'LUS'], roots: ['Sunquartz', 'Voidglass', 'Emberlight'], commodity: 'crystal' },
  { key: 'stone', cls: 'minerals', label: 'Stone Grade', stats: ['DEN', 'RES'], roots: ['Thanegran', 'Coldbasalt', 'Weirstone'], commodity: 'stone' },
  // ── chemicals ──
  { key: 'reagent', cls: 'chemicals', label: 'Reagent', stats: ['PUR', 'VOL'], roots: ['Vitriol', 'Lumen Salt', 'Marrowlye'], commodity: 'reagent' },
  { key: 'gas', cls: 'chemicals', label: 'Rare Gas', stats: ['VOL', 'RSN'], roots: ['Aetherine', 'Frostvapor', 'Palefume'], commodity: 'gas' },
  { key: 'solvent', cls: 'chemicals', label: 'Solvent', stats: ['PUR', 'RSN'], roots: ['Clearbrine', 'Ashwater', 'Quickmilk'], commodity: 'solvent' },
  // ── food ──
  { key: 'grain', cls: 'food', label: 'Grain', stats: ['NUT', 'PUR'], roots: ['Amberwheat', 'Duskbarley', 'Rimeoat'], commodity: 'grain' },
  { key: 'produce', cls: 'food', label: 'Produce', stats: ['NUT', 'FLX'], roots: ['Marshberry', 'Palefruit', 'Embergourd'], commodity: 'produce' },
  { key: 'game', cls: 'food', label: 'Game Meat', stats: ['NUT', 'RES'], roots: ['Bruskmeat', 'Fenhare', 'Cragmutton'], commodity: 'game' },
  { key: 'brine_catch', cls: 'food', label: 'Brine Catch', stats: ['NUT', 'PUR'], roots: ['Silverfin', 'Duskeel', 'Lanternfish'], commodity: 'fish' },
  // ── materials ──
  { key: 'fiber', cls: 'materials', label: 'Fiber', stats: ['FLX', 'RES'], roots: ['Windflax', 'Thistlecord', 'Mireweave'], commodity: 'fiber' },
  { key: 'hide', cls: 'materials', label: 'Hide', stats: ['RES', 'FLX'], roots: ['Thornhide', 'Fenleather', 'Palepelt'], commodity: 'hide' },
  { key: 'timber', cls: 'materials', label: 'Timber', stats: ['DEN', 'FLX'], roots: ['Greyoak', 'Emberpine', 'Duskwillow'], commodity: 'timber' },
  { key: 'resin', cls: 'materials', label: 'Resin', stats: ['VOL', 'FLX'], roots: ['Pitchgum', 'Amberweep', 'Coldtar'], commodity: 'resin' },
];

export const FAMILY_BY_KEY: Record<string, ResFamily> = Object.fromEntries(RES_FAMILIES.map((f) => [f.key, f]));

// Avalanche-finalized roll (the versona-names lesson): raw FNV leaves salts
// differing only in a trailing char CORRELATED in the high bits — epochs 1/2/3
// would pick the same roots and near-identical stats.
function rMix(h: number): number {
  h ^= h >>> 16; h = Math.imul(h, 0x85ebca6b); h ^= h >>> 13; h = Math.imul(h, 0xc2b2ae35); h ^= h >>> 16;
  return h >>> 0;
}
const h01r = (...parts: Array<string | number | bigint>): number => rMix(fnv(parts.join('|'))) / 0xffffffff;

/** The spawn's LODE CENTER — every rotation the vein MOVES (SWG's shifting
 *  planets): a deterministic region ±800 km derived from the seed. The
 *  survey points at it; concentration falls off away from it. */
export function lodeCenterOf(spawnSeed: number): { x: number; z: number; radius: number } {
  return {
    x: (h01r(spawnSeed, 'lx') - 0.5) * 1_600_000,
    z: (h01r(spawnSeed, 'lz') - 0.5) * 1_600_000,
    radius: 250_000 + h01r(spawnSeed, 'lr') * 200_000,
  };
}

/** Deterministic spawn NAME: family root + an epoch-salted suffix syllable. */
export function spawnNameOf(familyKey: string, epoch: number): string {
  const fam = FAMILY_BY_KEY[familyKey];
  if (!fam) return 'Unknown';
  const root = fam.roots[Math.floor(h01r(familyKey, 'root', epoch) * fam.roots.length) % fam.roots.length];
  const SUFF = ['Vein', 'Reach', 'Drift', 'Bloom', 'Seam', 'Run', 'Hollow', 'Flow'];
  const suff = SUFF[Math.floor(h01r(familyKey, 'suff', epoch) * SUFF.length) % SUFF.length];
  return `${root} ${suff}`;
}

/** Deterministic 1–1000 stat rolls for a spawn epoch (0 for stats the
 *  family does not carry — the SWG dash). Skewed so 900+ is RARE. */
export function rollSpawnStats(familyKey: string, epoch: number): Record<ResStat, number> {
  const fam = FAMILY_BY_KEY[familyKey];
  const out = {} as Record<ResStat, number>;
  for (const s of RES_STATS) out[s] = 0;
  if (!fam) return out;
  for (const s of fam.stats) {
    const a = h01r(familyKey, 'stat', s, epoch);
    const b = h01r(familyKey, 'stat2', s, epoch);
    // Average of two rolls: bell-ish around 500, tails reachable.
    out[s] = Math.max(1, Math.min(1000, Math.round(((a + b) / 2) * 1000)));
  }
  return out;
}

/**
 * The concentration field 0..1 at a commons position for a spawn — cheap
 * layered value-noise on the spawn's own seed. Deterministic everywhere:
 * the client SURVEYS with the same function the server pays out with.
 */
export function concentrationAt(spawnSeed: number, x: number, z: number): number {
  const cell = (gx: number, gz: number, s: number): number => h01r(spawnSeed, 'c', s, gx, gz);
  let v = 0, amp = 0.55, scale = 1 / 9000;
  for (let o = 0; o < 3; o++) {
    const gx = Math.floor(x * scale), gz = Math.floor(z * scale);
    const fx = x * scale - gx, fz = z * scale - gz;
    const sx = fx * fx * (3 - 2 * fx), sz = fz * fz * (3 - 2 * fz);
    const v00 = cell(gx, gz, o), v10 = cell(gx + 1, gz, o), v01 = cell(gx, gz + 1, o), v11 = cell(gx + 1, gz + 1, o);
    v += amp * ((v00 * (1 - sx) + v10 * sx) * (1 - sz) + (v01 * (1 - sx) + v11 * sx) * sz);
    amp *= 0.5;
    scale *= 2.3;
  }
  // REGIONAL LODE: the vein lives somewhere — concentration decays away
  // from the seed-derived center (geography matters; rotations MOVE it).
  const lode = lodeCenterOf(spawnSeed);
  const d = Math.hypot(x - lode.x, z - lode.z) / lode.radius;
  const lodeF = 0.12 + 0.88 * Math.exp(-d * d);
  // Sharpen: most of the map is thin; hotspots are worth FINDING (SWG law).
  return Math.max(0, Math.min(1, Math.pow(v * lodeF, 2.1) * 1.6));
}

/** Extractor sizes: storage + base rate per hour (BER) by structure type. */
export const EXTRACTOR_SPECS: Record<string, { capacity: number; ratePerH: number }> = {
  extractor_small: { capacity: 300, ratePerH: 45 },
  extractor_medium: { capacity: 900, ratePerH: 120 },
  extractor_large: { capacity: 2400, ratePerH: 280 },
};

/** Spawn lifetime bounds (s): SWG's 6–21 days at atlas pace. */
export const SPAWN_LIFE_MIN_S = 36 * 3600;
export const SPAWN_LIFE_MAX_S = 96 * 3600;
/** Active spawns kept PER FAMILY on the commons. */
export const SPAWNS_PER_FAMILY = 2;
/** Hand-sampling: base units per successful sample at 100% concentration. */
export const SAMPLE_BASE_UNITS = 14;
export const SAMPLE_MIN_CONC = 0.12;
